import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import moment from 'moment-timezone';
import { uniq } from 'lodash';

import { MentionModel } from '../../../../api/mentions/model/mention.model';
import { ContactsPublicApi } from '../../../../api/targeting/contacts-public.api';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MentionMetadataTypesEnum } from '../../../../Monitor/Shared/enums/mention-metadata-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { MentionContentCustomizationEnum } from '../../../../Common/Mentions/enums/mention-content-customization.enum';
import { KeywordHighlightModel } from '../../../../Common/Misc/models/keyword-highlight.model';
import { InsightsDisplayNameEnum } from '../../../../Common/Mentions/enums/insights-display-name.enum';
import { getSocialMediaTypes, MediaTypeEnum, mediaTypeIconMap, mediaTypeNoHeadlineMap } from '../../../../Common/Basic/enums/media-type.enum';
import { TwitterSubTypeEnum } from '../../../../Common/Basic/enums/twitter-sub-type.enum';
import { LinkedinSubTypeEnum } from '../../../../Common/Basic/enums/linkedin-sub-type.enum';
import { ImageSizeEnum } from '../../../../Monitor/Shared/enums/image-size.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { MentionCardHeightEnum } from '../../../../Common/Mentions/enums/mention-card-height.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

class EngagementDataModel {
    label: string;
    value: number;
}

const tvEyesDomains = ['http://mms.tveyes.com/', 'https://mms.tveyes.com/', 'http://archive.tveyes.com/'];
const tvEyesArchiveDomain = 'http://archive.tveyes.com/';

@Component({
    selector: 'agpr-mention-grid-display',
    templateUrl: './mention-grid-display.component.html',
    styleUrls: ['./mention-grid-display.component.scss'],
    standalone: false,
})
export class MentionGridDisplayComponent implements OnChanges {
    @Input() id: string = '';
    @Input() data: any;
    @Output() viewAllMentionsClicked = new EventEmitter<void>();

    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly metadataTypes = MentionMetadataTypesEnum;
    readonly mediaTypeIconMap = mediaTypeIconMap;
    readonly mediaTypeNoHeadlineMap = mediaTypeNoHeadlineMap;
    readonly mentionCardHeightEnum = MentionCardHeightEnum;
    readonly metadataEnumToMentionField: Map<MentionMetadataTypesEnum, string> = new Map<MentionMetadataTypesEnum, string>(
        [
            [MentionMetadataTypesEnum.audienceReach, 'audienceReach'],
            [MentionMetadataTypesEnum.sentiment, 'sentiment'],
            [MentionMetadataTypesEnum.location, 'geographyDisplayString'],
            [MentionMetadataTypesEnum.wordCount, 'wordCount'],
            [MentionMetadataTypesEnum.duration, 'clipDuration'],
            [MentionMetadataTypesEnum.ave, 'adValueUsd'],
            [MentionMetadataTypesEnum.keywords, 'bodyKeywords'],
            [MentionMetadataTypesEnum.domainAuthority, 'domainAuthority'],
        ]
    );
    readonly mentionContentTypes = MentionContentCustomizationEnum;
    readonly sentimentTypes = SearchCriteriaQuerySentimentsEnum;
    readonly insightsNameEnum = InsightsDisplayNameEnum;
    readonly imageSizeEnum = ImageSizeEnum;
    readonly socialMediaTypes = getSocialMediaTypes();
    readonly severity = messageSeverityEnum;
    sentimentMap: Record<string, string> = {};
    mentionUrlMap: Record<string, string> = {};
    bodyTextMap: Record<string, string> = {};
    highlightKeywordsMap: Record<string, KeywordHighlightModel[]> = {};
    engagementDataMap: Record<string, EngagementDataModel[]> = {};
    avatarPlaceholderTextMap: Record<string, string> = {};
    thumbnailImageMap: Map<string, string | null> = new Map();
    mentions: MentionModel[] = [];
    gridClass: string = 'three-columns';
    container: ElementRef<HTMLDivElement>;

    constructor(
        private ngZone: NgZone,
        private el: ElementRef,
        public contactsPublicApi: ContactsPublicApi,
        private cd: ChangeDetectorRef,
        private localizationService: LocalizationService,
    ) {}

    @ViewChild('container', { static: false }) set containerRef(el: ElementRef<HTMLDivElement> | undefined) {
        if (!el) {
            return;
        }

        this.container = el;
        this.updateGridClass();
        const ro = new ResizeObserver(([entry]) => {
            this.updateGridClass(entry.contentRect.width);
            this.resizeAllGridItems();
            this.cd.detectChanges();
        });

        ro.observe(el.nativeElement);
        this.ngZone.run(() => {
            this.resizeAllGridItems();
        });
    }

    @HostListener('window:resize', [])
    onResize(): void {
        this.updateGridClass();
        this.resizeAllGridItems();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.mentions = [];
        this.thumbnailImageMap = new Map<string, string | null>();

        if (this.data?.mentions) {
            this.data.mentions.forEach(mention => {
                this.mentions.push(mention);
            });
            if (this.data.mentionPreviewCount) {
                this.mentions = this.mentions.slice(0, this.data.mentionPreviewCount);
            }
            this.mentions.forEach(mention => {
                const mentionId = mention.id || mention.mercuryId;
                this.sentimentMap[mentionId] = this.getSentimentName(mention.sentiment);
                this.mentionUrlMap[mentionId] = this.getMentionUrl(mention);
                this.setBodyText(mention);
                this.setHighlightKeywords(mention);
                this.setEngagementData(mention);
                // For social mention, get its avatar placeholder text for outlet icon
                if (mention.socialData) {
                    this.avatarPlaceholderTextMap[mentionId] = this.getAvatarPlaceholderText(mention);
                }

                firstValueFrom(this.getThumbnailImage(mention)).then(url => {
                    this.thumbnailImageMap.set(mentionId, url);
                    this.cd.detectChanges();
                });
            });
        }
        this.resizeAllGridItems();
        this.cd.detectChanges();
    }
    // This follows the same logic used by the Mention model: src/app/api/mentions/model/mention.model.ts
    // to generate avatar placeholder text for social mentions.
    private getAvatarPlaceholderText(mention: MentionModel): string {
        const mediaType = mention.mediaType;
        const handle = mention.socialData?.handle;
        const tweetData = mention.socialData?.tweetData;
        const linkedinData = mention.socialData?.linkedinData;

        let placeholderText: string;
        switch (mediaType) {
            case MediaTypeEnum.TWITTER:
                placeholderText = 'X';
                break;
            case MediaTypeEnum.TIKTOK:
                placeholderText = 'T';
                break;
            case MediaTypeEnum.INSTAGRAM:
                placeholderText = 'I';
                break;
            case MediaTypeEnum.FACEBOOK:
                placeholderText = 'F';
                break;
            case MediaTypeEnum.REDDIT:
                placeholderText = 'R';
                break;
            case MediaTypeEnum.BLUESKY:
                placeholderText = 'B';
                break;
            case MediaTypeEnum.YOUTUBE:
                placeholderText = 'Y';

                if (mention.contactName) {
                    placeholderText = mention.contactName.charAt(0).toUpperCase();
                }
                break;
        }

        if (handle && mediaType !== MediaTypeEnum.YOUTUBE) {
            placeholderText = handle.charAt(0).toUpperCase();
        }

        if (tweetData?.type === TwitterSubTypeEnum.RETWEET && tweetData?.originalTweetData?.originalUserHandle) {
            placeholderText = tweetData.originalTweetData.originalUserHandle.charAt(0).toUpperCase();
        }

        if (linkedinData?.type === LinkedinSubTypeEnum.REPOST && linkedinData?.originalActivityData?.originalUserHandle) {
            placeholderText = linkedinData?.originalActivityData?.originalUserHandle.charAt(0).toUpperCase();
        }

        return placeholderText;
    }

    getThumbnailImage(model: MentionModel): Observable<string | null> {
        const mention = new Mention(model);


        if (model.images?.length > 0) {
            return of(model.images[0].url);
        }


        if (mention.isTvEyes()) {
            const parsedUrl = new URL(mention.url);

            switch (parsedUrl.hostname) {
                case 'mms.tveyes.com':
                    const thumbnailUrl = new URL('https://mms.tveyes.com/library/findThumbnail.asp');
                    thumbnailUrl.searchParams.append('StationID', parsedUrl.searchParams.get('StationID'));
                    thumbnailUrl.searchParams.append('PartnerID', parsedUrl.searchParams.get('PartnerID'));

                    // NOTE: the thumbnail requires a different date format than the playerlette url provides
                    const startDate = moment(parsedUrl.searchParams.get('StartDateTime'));
                    thumbnailUrl.searchParams.append('DateTime', startDate.format('MM/DD/YYYY HH:mm:ss'));

                    return of(thumbnailUrl.toString());

                case 'archive.tveyes.com':
                    parsedUrl.protocol = 'https';
                    return of(parsedUrl.toString().replace(/\.mp[34]$/i, '.jpg'));

                default:
                    return of(null);
            }
        }

        if (mention.tvEyesPodcastID && mention.thumbnailUrl) {
            return of(mention.thumbnailUrl);
        }


        if (model.outletAvatarUrl) {
            return of(model.outletAvatarUrl);
        }

        if (model.contactAvatarUrl) {
            return of(model.contactAvatarUrl);
        }

        if (mention.author?.uuid) {
            return this.contactsPublicApi.getContact(mention.author.uuid).pipe(
                map(contact => contact?.avatarUrl ?? null),
                catchError(() => of(null))
            );
        }

        return of(null);
    }

    // Mirrors the TVEyes InstaPlay override in twig.extensions.ts's mention_url filter
    private getMentionUrl(mention: MentionModel): string {
        const hasUrl = typeof mention.url === 'string';
        const isTVEyesMentionURL = hasUrl && !!tvEyesDomains.find(domain => mention.url.startsWith(domain));
        const isTVEyesArchiveMention = isTVEyesMentionURL && mention.url.startsWith(tvEyesArchiveDomain);
        const hasTVEyesInstaPlayUrl = !!mention.tvEyesInstaPlayUrl;
        const enforceConcisePlayer = !!this.data?.styles?.enforceConcisePlayer;

        let mentionUrl = mention.url;

        if (isTVEyesMentionURL && !isTVEyesArchiveMention && hasTVEyesInstaPlayUrl && !enforceConcisePlayer) {
            mentionUrl = mention.tvEyesInstaPlayUrl as string;
        }

        return mentionUrl;
    }

    onImageError(ev: Event, mentionId: string) {
        this.thumbnailImageMap.set(mentionId, null);

        // Hide immediately (removes broken-image icon right away)
        const img = ev.target as HTMLImageElement;
        img.src = '';
        img.style.display = 'none';
    }

    updateGridClass(width?: number) {
        if (!this.container) {
            return;
        }

        const w = width ?? this.container.nativeElement.offsetWidth;
        this.ngZone.run(() => {
            this.gridClass = w > 957
                ? 'three-columns'
                : w > 480
                    ? 'two-columns'
                    : 'one-column';
        });
    }

    viewAllMentions() {
        this.viewAllMentionsClicked.emit();
    }

    getSentimentName(value: number): string {
        let sentiment = 'Sentiment.NOT_AVAILABLE';

        if (value === null) {
            sentiment = 'Sentiment.NOT_AVAILABLE';
        } else if (value >= -1 && value <= -0.3) {
            sentiment = 'Sentiment.NEGATIVE';
        } else if (value >= -0.2 && value <= 0.2) {
            sentiment = 'Sentiment.NEUTRAL';
        } else if (value >= 0.3) {
            sentiment = 'Sentiment.POSITIVE';
        }

        return sentiment;
    }
    setBodyText(mention: MentionModel) {
        const mentionId = mention.id || mention.mercuryId;
        let bodyLength: number = mention?.body?.length || 0;
        const body = mention.isTranslatedPreferred && mention.translation.body ? mention.translation.body : mention.body;
        const summary = mention.isTranslatedPreferred && mention.translation.summary ? mention.translation.summary : mention.summary;
        if (this.data.styles.mentionContent === this.mentionContentTypes.fullText) {
            this.bodyTextMap[mentionId] = body;
        } else if (this.data.styles.mentionContent === this.mentionContentTypes.lead) {
            this.bodyTextMap[mentionId] = body?.slice(0, 256);
        } else if (this.data.styles.mentionContent === this.mentionContentTypes.summary) {
            if (summary) {
                bodyLength = mention.summary.length;
                this.bodyTextMap[mentionId] = summary.slice(0, 256);
            } else {
                this.bodyTextMap[mentionId] = body?.slice(0, 256);
            }
        }
        if (bodyLength && (this.data.styles.mentionContent !== this.mentionContentTypes.headlineOnly) && (bodyLength > this.bodyTextMap[mentionId]?.length)) {
            this.bodyTextMap[mentionId] += '...';
        }
    }
    setHighlightKeywords(mention: MentionModel) {
        const mentionId = mention.id || mention.mercuryId;
        let contextualKeywords = [];
        this.highlightKeywordsMap[mentionId] = [];
        mention.contexualKeywordIndices?.forEach(index => {
            const keyword = mention.body.substring(index.start, index.end);
            if (!contextualKeywords.some(item => item.toLowerCase() === keyword.toLowerCase())) {
                contextualKeywords.push(keyword);
            }
        });

        mention.summaryContextualKeywordIndices?.forEach(index => {
            const keyword = mention.summary.substring(index.start, index.end);
            if (!contextualKeywords.some(item => item.toLowerCase() === keyword.toLowerCase())) {
                contextualKeywords.push(keyword);
            }
        });

        contextualKeywords = uniq([...contextualKeywords, ...(mention.matchedKeywords || [])]);

        this.highlightKeywordsMap[mentionId].push({
            class: 'agpr-highlight',
            caseSensitive: false,
            keywords: [...contextualKeywords]
        });

        mention.bodyKeywords = contextualKeywords.join(', ');
    }

    setEngagementData(model: MentionModel) {
        const mention = new Mention(model);
        const engagementAttribute: EngagementDataModel = {
            label: 'Mention.details.engagements',
            value: mention.engagements
        };
        const engagementRateAttribute: EngagementDataModel = {
            label: 'Mention.details.engagementRate',
            value: +mention.engagementRate.toFixed(3),
        };

        const likesAttribute: EngagementDataModel = {
            label: 'Mention.details.likes',
            value: mention.like,
        };

        const upvotesAttribute: EngagementDataModel = {
            label: 'Mention.details.upvotes',
            value: mention.like,
        };

        const commentsAttribute: EngagementDataModel = {
            label: 'Mention.details.comments',
            value:mention.reply,
        };

        const repliesAttribute: EngagementDataModel = {
            label: 'Mention.details.replies',
            value: mention.reply,
        };

        const sharesAttribute: EngagementDataModel = {
            label: 'Mention.details.shares',
            value: mention.share,
        };

        const repostsAttribute: EngagementDataModel = {
            label: 'Mention.details.retweets',
            value: mention.share,
        };

        const viewsAttribute: EngagementDataModel = {
            label: 'Mention.details.views',
            value: mention.view,
        };

        const quotesAttribute: EngagementDataModel = {
            label: 'Mention.details.quotes',
            value: mention.quote,
        };

        this.engagementDataMap[model.id] = [
            engagementAttribute,
            engagementRateAttribute,
        ];

        switch (mention.mediaType.id) {
            case MediaTypeEnum.TWITTER:
                this.engagementDataMap[model.id].push(
                    repostsAttribute,
                    quotesAttribute,
                    repliesAttribute,
                    likesAttribute,
                    viewsAttribute
                );
                break;
            case MediaTypeEnum.INSTAGRAM:
                this.engagementDataMap[model.id].push(
                    sharesAttribute,
                    commentsAttribute,
                    likesAttribute
                );
                break;
            case MediaTypeEnum.REDDIT:
                this.engagementDataMap[model.id].push(
                    sharesAttribute,
                    commentsAttribute,
                    upvotesAttribute,
                    viewsAttribute
                );
                break;
            case MediaTypeEnum.LINKEDIN:
                this.engagementDataMap[model.id].push(
                    commentsAttribute,
                    likesAttribute
                );
                break;
            case MediaTypeEnum.BLUESKY:
                this.engagementDataMap[model.id].push(
                    likesAttribute,
                    quotesAttribute,
                    repliesAttribute,
                    repostsAttribute
                );
                break;
            default:
                this.engagementDataMap[model.id].push(
                    sharesAttribute,
                    commentsAttribute,
                    likesAttribute,
                    viewsAttribute
                );
                break;
        }
    }
    scrollToTop() {
        document.getElementById(`${this.id}`)?.scrollIntoView({ behavior: 'smooth' });
    }

    resizeGridItem(item){
        if ((this.data?.styles?.cardHeight === MentionCardHeightEnum.flexible) && item) {
            const grid = this.el.nativeElement.getElementsByClassName('agpr-mentionGridDisplay')?.[0];
            if (grid) {
                item.style.height = 'auto';
                const rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-row-gap'), 10);

                const newHeight = Math.ceil(item.getBoundingClientRect().height / rowGap) * rowGap;
                const rowSpan = Math.ceil((item.getBoundingClientRect().height + rowGap) / (rowGap));

                item.style.gridRowEnd = `span ${Math.max(rowSpan, 0)}`;
                item.style.height = `${newHeight}px`;
            }
        } else if (item) {
            item.style.height = '100%';
            item.style.gridRowEnd = null;
        }
    }

    resizeAllGridItems(){
        const allItems = this.el.nativeElement.getElementsByClassName('agpr-mentionGridDisplay-card');
        for (let x = 0; x < allItems?.length; x++){
            setTimeout(() => this.resizeGridItem(allItems[x]), 0);
        }
    }
}
